import { Flex, Loader } from "@mantine/core";
import { IAzureBlobFile } from "@schemas/azureSchema";
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";

const FileUploader: React.FC<{
  onFileUpload: (file: File) => void;
  files: IAzureBlobFile[];
  imageOnly?: boolean;
  disabled?: boolean;
  loading?: boolean;
}> = ({ onFileUpload, files, imageOnly = false, disabled = false, loading = false }) => {
  const maxSize = 20 * 1024 * 1024; // 20MB in bytes
  const minSize = 1; // 1 byte
  const acceptedFileTypes = imageOnly
    ? ["image/jpeg", "image/png", "image/gif", "image/jpg"]
    : [
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel.sheet.macroEnabled.12",
        "text/plain",
        "video/mp4",
        "application/vnd.ms-powerpoint", // MIME type for .ppt
        "application/vnd.ms-outlook", // MIME type for .msg
        "message/rfc822", // MIME type for .eml
        "application/vnd.ms-powerpoint", // MIME type for .ppt
        "application/vnd.openxmlformats-officedocument.presentationml.presentation", // MIME type for .pptx
      ];

  const checkFileExtension = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    return ["msg", "xlsm"].includes(extension ?? "");
  };

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      if (acceptedFiles?.length + files?.length >= 20) {
        alert(`For mange filer. Kan kun laste opp 20 filer totalt.`);
        return;
      }

      if (acceptedFiles?.length > 0) {
        acceptedFiles.forEach((file: File) => {
          const isValidType = acceptedFileTypes.includes(file.type) || checkFileExtension(file.name);
          if (isValidType) {
            onFileUpload(file);
          } else {
            alert(`Ugyldig filtype.`);
          }
        });
      }

      fileRejections.forEach((file) => {
        if (file.size > maxSize) {
          alert(`Filen er for stor. Maks tillatte størrelse er 20MB.`);
        } else if (file.size < minSize) {
          alert(`Filen er tom.`);
        }
      });
    },
    [onFileUpload, files],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxSize,
    minSize,
  });

  return (
    <Flex
      mb="xs"
      align="center"
      justify="center"
      {...getRootProps()}
      className={`dropzone ${disabled ? "disabled" : ""}`}
      style={{ cursor: disabled ? "not-allowed" : "pointer" }}
    >
      <input {...getInputProps()} disabled={disabled} />
      <div>{loading ? <Loader /> : "Dra og slipp filer her, eller klikk for å velge filer (Maks størrelse: 20MB)"}</div>
    </Flex>
  );
};

export default FileUploader;
